import { useState, useRef, useEffect, useCallback } from 'react';
import { Icon, type IconKey } from '../../icons';
import {
  OFF, ROAD_SEG, layerLabel, privacyLabel, type LayerRow, type LayerId, type PrivacyMode,
} from './aiLayers';

interface AiLayerMenuProps {
  rows: LayerRow[];
  active: LayerId;
  /** A switch is in flight — every control is inert until it lands. */
  busy: boolean;
  error: string | null;
  onSelect: (id: LayerId) => void;

  roadSegMode: 'blur' | 'overlay';
  onSwitchRoadSegMode: () => void;
  onOpenRoadSegSettings: () => void;

  geoMaskEnabled: boolean;
  geoMaskMode: 'blur' | 'outline';
  geoMaskAltSource: 'elevation' | 'height' | 'computed' | 'altitude';
  geoMaskYawOffset: number;
  geoMaskCameraLabel: string;
  geoMaskPitchOffset: number;
  /** Operator-set camera height above the road; null = from the ALT source. */
  geoMaskHeightM: number | null;
  /** Why the server will not project (it then blurs everything), or null. */
  geoMaskRefused: string | null;
  onToggleGeoMask: () => void;
  onSwitchGeoMaskMode: () => void;
  onCycleGeoMaskAlt: () => void;
  onAdjustYawOffset: (delta: number) => void;
  onAdjustPitchOffset: (delta: number) => void;
  onSetGeoMaskHeight: (heightM: number | null) => void;

  /** What this feed's picture shows to viewers — see `feedPrivacy`. */
  privacyMode: PrivacyMode;
  /** The host confirms a switch back to `picture` and writes it; this only asks. */
  onSelectPrivacy: (mode: PrivacyMode) => void;
  /** A privacy write is in flight. */
  privacyBusy?: boolean;
  privacyError?: string | null;
  /** Why this feed cannot take a privacy mode (decoded in the browser), or null. */
  privacyUnavailable?: string | null;

  /** The host's own button class — the docked and floating headers style differently. */
  buttonClass?: string;
  /** Icon-only button, for the cramped floating header. */
  compact?: boolean;
}

const PRIVACY_ROWS: { mode: PrivacyMode; sub: string }[] = [
  { mode: 'picture', sub: 'full video to viewers' },
  { mode: 'blur', sub: 'heads blurred in every picture that leaves' },
  { mode: 'data_only', sub: 'no picture leaves: avatars, counts, lines' },
];

function iconFor(active: LayerId): IconKey {
  return active === ROAD_SEG ? 'roadseg' : 'detect';
}

/**
 * The single control that replaced DETECT, ROAD SEG (+ mode, + settings) and
 * GEO MASK (+ mode, + altitude source, + yaw nudges) in the video header.
 *
 * The AI layer is a radio group because only one can run: `marketplace_runtime`
 * keeps a single process-global active slug, and road segmentation shares the
 * same inference budget. The geographic mask is a checkbox in its own section
 * because it composes with all of them — it projects OSM polygons through the
 * camera pose rather than running a model.
 *
 * Purely presentational. Every mutation is the host's, so this component cannot
 * disagree with the server about what is running.
 */
export default function AiLayerMenu({
  rows, active, busy, error, onSelect,
  roadSegMode, onSwitchRoadSegMode, onOpenRoadSegSettings,
  geoMaskEnabled, geoMaskMode, geoMaskAltSource, geoMaskYawOffset, geoMaskCameraLabel,
  geoMaskPitchOffset, geoMaskHeightM, geoMaskRefused,
  onToggleGeoMask, onSwitchGeoMaskMode, onCycleGeoMaskAlt, onAdjustYawOffset,
  onAdjustPitchOffset, onSetGeoMaskHeight,
  privacyMode, onSelectPrivacy, privacyBusy = false, privacyError = null, privacyUnavailable = null,
  buttonClass = 'detection-toggle', compact = false,
}: AiLayerMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [heightDraft, setHeightDraft] = useState(geoMaskHeightM === null ? '' : String(geoMaskHeightM));

  useEffect(() => {
    setHeightDraft(geoMaskHeightM === null ? '' : String(geoMaskHeightM));
  }, [geoMaskHeightM]);

  // Blank clears the override; anything outside 1-500 m is reverted, not sent.
  const commitHeight = useCallback(() => {
    const text = heightDraft.trim();
    if (text === '') {
      if (geoMaskHeightM !== null) onSetGeoMaskHeight(null);
      return;
    }
    const value = Number(text);
    if (!Number.isFinite(value) || value < 1 || value > 500) {
      setHeightDraft(geoMaskHeightM === null ? '' : String(geoMaskHeightM));
      return;
    }
    if (value !== geoMaskHeightM) onSetGeoMaskHeight(value);
  }, [heightDraft, geoMaskHeightM, onSetGeoMaskHeight]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openSettings = useCallback(() => {
    setOpen(false);
    onOpenRoadSegSettings();
  }, [onOpenRoadSegSettings]);

  // The visible text is the running layer's name, which changes, and the compact
  // variant has none at all — hence the fixed aria-label on the trigger.
  const label = layerLabel(rows, active);
  const running = active !== OFF;

  return (
    <div className="ai-layer-control" ref={rootRef}>
      <button
        className={`${buttonClass} ${running ? 'active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={running ? `${label} is running — click to change` : 'Choose an AI layer for this feed'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="AI layer"

      >
        <Icon name={iconFor(active)} />
        {!compact && <span className="ai-layer-label">{label}</span>}
        <span className="ai-layer-caret"><Icon name="chev-down" /></span>
      </button>

      {open && (
        <div
          className={`ai-layer-menu ${compact ? 'align-right' : ''}`}
          role="menu"
          // The floating panel starts a window drag on header mousedown and
          // preventDefault()s it. Its guard only skips <button>, so a click on a
          // radio in here would be eaten. Nothing inside this menu is the
          // panel's to drag.
          onMouseDown={e => e.stopPropagation()}
        >
          <div className="ai-layer-section">
            <span className="ai-layer-section-title">AI layer</span>
            <span className="ai-layer-note">affects all viewers</span>
          </div>

          {rows.map(row => {
            const checked = active === row.id;
            // A row that cannot run is still shown — an operator who cannot see
            // the locked model cannot ask to buy it. It stays clickable only if
            // it is somehow already the running layer.
            const disabled = busy || (!row.selectable && !checked);
            return (
              <div key={row.id}>
                <label
                  className={`ai-layer-row ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
                  title={row.description || row.name}
                >
                  <input
                    type="radio"
                    name="ai-layer"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onSelect(row.id)}
                  />
                  <span className="ai-layer-name">{row.name}</span>
                  {row.reason && <span className="ai-layer-badge">{row.reason}</span>}
                </label>

                {row.id === ROAD_SEG && checked && (
                  <div className="ai-layer-sub">
                    <button
                      className="detection-toggle"
                      onClick={onSwitchRoadSegMode}
                      title={`Switch to ${roadSegMode === 'blur' ? 'overlay' : 'blur'} mode`}
                    >
                      {roadSegMode === 'blur' ? 'Blurring' : 'Overlay'}
                    </button>
                    <button className="detection-toggle" onClick={openSettings} title="Road segmentation settings">
                      Settings
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {error && <div className="ai-layer-error">{error}</div>}

          <div className="ai-layer-section">
            <span className="ai-layer-section-title">Privacy</span>
          </div>

          {PRIVACY_ROWS.map(({ mode, sub }) => {
            const checked = privacyMode === mode;
            const disabled = busy || privacyBusy || !!privacyUnavailable;
            return (
              <div key={mode}>
                <label className={`ai-layer-row ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}>
                  <input
                    type="radio"
                    name="feed-privacy"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onSelectPrivacy(mode)}
                  />
                  <span className="ai-layer-name">{privacyLabel(mode)}</span>
                </label>
                <div className="ai-layer-sub ai-layer-note">{sub}</div>
              </div>
            );
          })}

          {privacyUnavailable && <div className="ai-layer-note">{privacyUnavailable}</div>}
          {privacyError && <div className="ai-layer-error">{privacyError}</div>}

          <label className={`ai-layer-row ${geoMaskEnabled ? 'checked' : ''}`}
                 title="Keep OpenStreetMap roads and public spaces sharp and blur the rest, projected through the camera pose">
            <input type="checkbox" checked={geoMaskEnabled} onChange={onToggleGeoMask} />
            <span className="ai-layer-name">Geo Mask (OSM)</span>
          </label>

          {geoMaskEnabled && (
            <div className="ai-layer-sub">
              <button
                className="detection-toggle"
                onClick={onSwitchGeoMaskMode}
                title={`Switch to ${geoMaskMode === 'blur' ? 'outline (diagnostic)' : 'blur'} mode`}
              >
                {geoMaskMode === 'blur' ? 'Blur' : 'Outline'}
              </button>
              <button
                className="detection-toggle"
                onClick={onCycleGeoMaskAlt}
                title={geoMaskHeightM !== null
                  ? 'Altitude source: manual camera height — clear the height to use telemetry'
                  : `Altitude source: ${geoMaskAltSource} — click to cycle`}
                disabled={geoMaskHeightM !== null}
                style={geoMaskAltSource !== 'elevation' && geoMaskHeightM === null
                  ? { backgroundColor: '#e67e22', borderColor: '#e67e22' } : undefined}
              >
                ALT:{geoMaskHeightM !== null ? 'MANUAL' : geoMaskAltSource.toUpperCase()}
              </button>
              <button
                className="detection-toggle ai-layer-nudge"
                onClick={() => onAdjustYawOffset(-10)}
                title="Rotate projection 10° left"
              >
                ◀◀
              </button>
              <button
                className="detection-toggle ai-layer-nudge"
                onClick={() => onAdjustYawOffset(-1)}
                title="Rotate projection 1° left"
              >
                ◀
              </button>
              <span className="ai-layer-yaw" title="Yaw offset (magnetic declination correction)">
                {geoMaskYawOffset > 0 ? '+' : ''}{geoMaskYawOffset}°
              </span>
              <button
                className="detection-toggle ai-layer-nudge"
                onClick={() => onAdjustYawOffset(1)}
                title="Rotate projection 1° right"
              >
                ▶
              </button>
              <button
                className="detection-toggle ai-layer-nudge"
                onClick={() => onAdjustYawOffset(10)}
                title="Rotate projection 10° right"
              >
                ▶▶
              </button>
              <span className="ai-layer-camera" title="Camera model (auto-detected from MQTT device)">
                {geoMaskCameraLabel}
              </span>
            </div>
          )}

          {geoMaskEnabled && (
            <div className="ai-layer-sub">
              <label className="ai-layer-yaw" title="Camera height above the road, in metres. Blank = from the ALT source (a parked aircraft reports 0)">
                H{' '}
                <input
                  type="number"
                  min={1}
                  max={500}
                  step={0.5}
                  inputMode="decimal"
                  aria-label="Camera height above road (m)"
                  placeholder="auto"
                  value={heightDraft}
                  onChange={(e) => setHeightDraft(e.target.value)}
                  onBlur={commitHeight}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitHeight(); }}
                  style={{ width: '4.5em' }}
                />
                {' '}m
              </label>
              <button
                className="detection-toggle ai-layer-nudge"
                onClick={() => onAdjustPitchOffset(0.5)}
                title="Tilt projection 0.5° up"
                aria-label="Pitch up"
              >
                ▲
              </button>
              <span className="ai-layer-yaw" title="Pitch trim added to the gimbal pitch">
                P{geoMaskPitchOffset > 0 ? '+' : ''}{geoMaskPitchOffset}°
              </span>
              <button
                className="detection-toggle ai-layer-nudge"
                onClick={() => onAdjustPitchOffset(-0.5)}
                title="Tilt projection 0.5° down"
                aria-label="Pitch down"
              >
                ▼
              </button>
            </div>
          )}

          {geoMaskEnabled && geoMaskRefused && (
            <div className="ai-layer-error" role="status">
              Geo mask blurring everything: {geoMaskRefused}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
